import React, { useCallback } from 'react';
import { useRouter } from 'expo-router';
import { View } from 'react-native';

import { useNavbarContainerHeight, useResponsive, webOnlyStyles } from '@sg/garnish';

import { HomeV2, useHomeContent } from '@order/features-home';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';
import { useUsableNetBannerHeight } from '@order/usable-net';

import { AppFooter, ScreenHeader } from '../../../../components';
import { useLastOrderStatus, useMenuReorderRailOrders, usePendingOrder } from '../../../../hooks';

export const HomeScreenPostOrder = (props: HomeScreenPostOrderProps) => {
  const {
    content,
    pendingOrder,
    pendingOrders,
    lastOrderStatus,
    hasPendingOrder,
    hasMultiplePendingOrders,
  } = props;

  const router = useRouter();
  const { track } = useTelemetry();
  const { currentBreakpoint: { isXS }, match } = useResponsive(); // prettier-ignore

  const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');
  const isLoyaltyUpdatesEnabled = useFeatureFlag('CELS-5711-order-loyalty-updates-enabled');

  // ─── Styles ────────────────────────────────────────────────────────────────

  // So that the banner takes up 70% of the height of the screen on larger viewports.
  const usableNetBannerHeight = useUsableNetBannerHeight();
  const fullHeightMinusUsableNet = `calc(100svh - ${usableNetBannerHeight}px)` as never;
  const postOrderHeight = match([null, webOnlyStyles({ minHeight: fullHeightMinusUsableNet })]); // prettier-ignore
  const { paddingBottomStyle: navbarPaddingBottomStyle } = useNavbarContainerHeight();

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  const viewOrders = useCallback(() => {
    track('home.post_order.view_order.selected');

    if (hasMultiplePendingOrders) {
      router.navigate('/account/orders');
      return;
    }

    if (hasPendingOrder) {
      router.navigate(`/order-status/${lastOrderStatus.orderId}`);
    }
  }, [router, hasMultiplePendingOrders, hasPendingOrder, lastOrderStatus, track]);

  const openRewards = useCallback(() => {
    track('home.post_order.points_cta.selected');
    router.navigate(isLoyaltyUpdatesEnabled ? '/loyalty' : '/rewards');
  }, [isLoyaltyUpdatesEnabled, router, track]);

  const postOrderBanner = match([content.postOrderBannerXs, content.postOrderBannerSm]);

  const trackPostOrderBannerCtaPress = useCallback(() => {
    track('home.post_order.campaign.selected', { campaignId: postOrderBanner.id });
  }, [track, postOrderBanner.id]);

  // ───────────────────────────────────────────────────────────────────────────

  const order = lastOrderStatus ?? pendingOrders[0];
  const product = order?.lineItems[0]?.product;
  const cardContent = hasMultiplePendingOrders ? content.postOrderCardMultiple : content.postOrderCard; // prettier-ignore
  const hasEarnedLoyaltyPoints = lastOrderStatus.hasEarnedLoyaltyPoints || pendingOrders.some((order) => order.hasEarnedLoyaltyPoints); // prettier-ignore

  return (
    <>
      {isNavbarV2Enabled && isXS ? (
        <ScreenHeader pendingOrder={pendingOrder} rounded={false} shouldShowLocationTooltip />
      ) : null}

      <HomeV2.Container>
        <HomeV2.PostOrderContent style={postOrderHeight}>
          {isXS ? (
            <>
              <HomeV2.OrderCard
                title={cardContent.title}
                description={cardContent.description}
                cta={cardContent.cta}
                image={product?.asset?.url}
                imageAlt={product?.name}
                orderCount={order.lineItems.length}
                onPress={viewOrders}
              />

              <HomeV2.OrderBanner
                title={postOrderBanner.title}
                description={postOrderBanner.description}
                image={postOrderBanner.image}
                imageAlt={postOrderBanner.imageA11y}
                deepLink={postOrderBanner.deepLink}
                foregroundColor={postOrderBanner.foregroundColor}
                cta={postOrderBanner.cta}
                onPress={trackPostOrderBannerCtaPress}
              />

              {hasEarnedLoyaltyPoints ? (
                <HomeV2.LoyaltyCard
                  title={content.postOrderLoyalty.title}
                  description={content.postOrderLoyalty.description}
                  cta={content.postOrderLoyalty.cta}
                  onPress={openRewards}
                />
              ) : null}
            </>
          ) : (
            <>
              <HomeV2.OrderBanner.Image
                image={postOrderBanner.image}
                imageAlt={postOrderBanner.imageA11y}
              />

              <HomeV2.PostOrderContent.Footer>
                <HomeV2.OrderBanner
                  title={postOrderBanner.title}
                  description={postOrderBanner.description}
                  deepLink={postOrderBanner.deepLink}
                  foregroundColor={postOrderBanner.foregroundColor}
                  cta={postOrderBanner.cta}
                  onPress={trackPostOrderBannerCtaPress}
                />

                <HomeV2.PostOrderContent.Info>
                  <HomeV2.OrderCard
                    title={cardContent.title}
                    description={cardContent.description}
                    cta={cardContent.cta}
                    image={product?.asset?.url}
                    imageAlt={product?.name}
                    orderCount={order.lineItems.length}
                    onPress={viewOrders}
                  />

                  {hasEarnedLoyaltyPoints ? (
                    <HomeV2.LoyaltyCard
                      title={content.postOrderLoyalty.title}
                      description={content.postOrderLoyalty.description}
                      cta={content.postOrderLoyalty.cta}
                      onPress={openRewards}
                    />
                  ) : null}
                </HomeV2.PostOrderContent.Info>
              </HomeV2.PostOrderContent.Footer>
            </>
          )}
        </HomeV2.PostOrderContent>

        {isNavbarV2Enabled && isXS ? <View style={navbarPaddingBottomStyle} /> : null}

        {isXS ? null : <AppFooter />}
      </HomeV2.Container>
    </>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type HomeScreenPostOrderProps = {
  content: ReturnType<typeof useHomeContent>;
  pendingOrder: ReturnType<typeof usePendingOrder>;
  lastOrderStatus: ReturnType<typeof useLastOrderStatus>;
  pendingOrders: ReturnType<typeof useMenuReorderRailOrders>['orders'];
  hasPendingOrder: boolean;
  hasMultiplePendingOrders: boolean;
};
